import { Table, Modal, Button, Col, Row, Dropdown } from "react-bootstrap";
import { BarreReseau, Distance, DownloadModal, engineStat, generateExcelFile, generatePDFFile, getAddressFromCoordinates, getReportName, handleDownloadConfirm, toTimestamp, ValidatePosition } from "../utilities/functions";
import { useTranslate } from "../hooks/LanguageProvider";
import ExcelJS, { Style } from "exceljs";
import MapModal from "../components/Map/MapModal";
import Linecharts, { ChartData } from "../components/Charts/Linecharts";
import { useEffect, useState, useRef } from "react";
import { NavLink, useParams } from "react-router-dom";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useClipboard } from "../utilities/functions";
import { PropagateLoader } from 'react-spinners'
import ReactPaginate from "react-paginate";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat"; // ✅ AJOUT
import { log } from "util";
import { toast } from "react-toastify";
dayjs.extend(customParseFormat);                                  // ✅ AJOUT

// ✅ formate en local (pas d'ISO/Z)
const fmtLocal = (d: Dayjs) => d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : '';

// ✅ nettoie une date passée en URL (enlève T/Z)
const normalizeUrlDate = (s: string) =>
  s ? s.replace('T', ' ').replace('Z', '').slice(0, 19) : s;

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface Position {
  id_position: number;
  PSN: string;
  TIMESTAMP: string; // format ISO (ex: "2025-05-10T10:17:32.000Z")
  ENGINESTAT: number;
  LAT: number;
  LON: number;
  ALT: number;
  SOG: number; // Speed Over Ground
  GSMLVL: number | null;
  NST: number;
  GPSDIST: number;
  COG: number; // Course Over Ground
  GUS: number;
}

export function PositionTracking() {
  const { from_date, to_date, search_term } = useParams<{
    from_date?: string;
    to_date?: string;
    search_term?: string;
  }>();

  const user_id = localStorage.getItem("userid");

  // ✅ init état avec fallback propre dès le premier render
  const initialStart = (): Dayjs => {
    if (from_date) {
      const d = dayjs(normalizeUrlDate(decodeURIComponent(from_date)), "YYYY-MM-DD HH:mm:ss", true);
      if (d.isValid()) return d;
    }
    return dayjs().startOf("day");
  };

  const initialEnd = (): Dayjs => {
    if (to_date) {
      const d = dayjs(normalizeUrlDate(decodeURIComponent(to_date)), "YYYY-MM-DD HH:mm:ss", true);
      if (d.isValid()) return d;
    }
    return dayjs().endOf("day");
  };
  const [startDate, setStartDate] = useState<Dayjs>(initialStart);
  const [endDate, setEndDate] = useState<Dayjs>(initialEnd);
  const [search, setSearch] = useState<string>(search_term ? decodeURIComponent(search_term) : "");
  const { translate } = useTranslate();
  const [positions, setPositions] = useState<Position[]>([]);
  const [pageCount, setPageCount] = useState(0);
  let [total, setTotal] = useState(0);
  let [limitValue, setSelectedLimitValue] = useState(10);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [column, setSortColumn] = useState("immatriculation_vehicule");
  const [sort, setSort] = useState("ASC");
  const [typeSearch, setTypeSearch] = useState("Immatriculation");
  const [selectedSearch, setSelectedSearch] = useState(translate("Matriculation"));
  const [type, setType] = useState(0);


  const [selectedColumns, setSelectedColumns] = useState({
    id_vehicule: true,
    immatriculation_vehicule: true,
    vehicule_type: true,
    id_dispositif: true,
    nom_groupe: true,
    username_user: true,
    PSN: true,
    GPSDIST: true,
  });

  const searchColumn: { [key: string]: number } = {
    id_vehicule: 0,
    vehicule_type: 1,
    immatriculation_vehicule: 2,
    PSN: 3,
    username_user: 4,
    nom_groupe: 5,
    odometer: 6,
  };

  const [reportData, setReportData] = useState<any>([]);
  const [modalInfo, setModalInfo] = useState<{ show: boolean; lat: number; lng: number; }>({ show: false, lat: 0, lng: 0 });

  const getPositions = async (
    limit: number,
    page: number,
    search: string,
    type: number,
    column: string,
    sort: string,
    from_date: string,
    to_date: string
  ) => {
    const bodyData = JSON.stringify({
      limit: limit,
      page: page,
      search: search,
      type: type,
      id_user: user_id,
      column: column,
      sort: sort,
      from_date,
      to_date
    });

    try {
      setLoading(true);

      const total_pages = await fetch(`${backendUrl}/api/position/totalpage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: bodyData,
        mode: "cors",
      });

      const totalPages = await total_pages.json();
      total = totalPages[0]?.["total"] ?? 0;
      setTotal(total);

      const vehicleResponse = await fetch(`${backendUrl}/api/position/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: bodyData,
        mode: "cors",
      });

      if (!vehicleResponse.ok) {
        console.error("Error retrieving vehicles list");
        setLoading(false);
        return;
      }

      const data = await vehicleResponse.json();
      setPageCount(Math.ceil(total / limit));
      setReportData(data);
      setPositions(data);
      return data;
    } catch (error) {
      console.error("Error retrieving data from table", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Premier chargement & navigation via URL : un seul appel propre (page=1)
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;


    setSearch(search_term ? decodeURIComponent(search_term) : search);
    let sDate = startDate;
    if (from_date) {
      const d = dayjs(normalizeUrlDate(decodeURIComponent(from_date)), "YYYY-MM-DD HH:mm:ss", true);
      if (d.isValid()) {
        sDate = d.subtract(2, "minute");
        setStartDate(sDate);
      }
    }

    let eDate = endDate;
    if (to_date) {
      const d = dayjs(normalizeUrlDate(decodeURIComponent(to_date)), "YYYY-MM-DD HH:mm:ss", true);
      if (d.isValid()) {
        eDate = d.add(2, "minute");
        setEndDate(eDate);
      }
    }

    // 🔁 appel unique, page=1
    getPositions(
      limitValue,
      1,
      search,
      type,
      column,
      sort,
      fmtLocal(sDate),
      fmtLocal(eDate)
    );
    setCurrentPage(1);
    setSearch(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ si l'URL change pendant que la page est ouverte → on se resynchronise
  useEffect(() => {
    if (!didInit.current) return;

    const s = search_term ? decodeURIComponent(search_term) : search;

    let sDate = startDate;
    if (from_date) {
      const d = dayjs(normalizeUrlDate(decodeURIComponent(from_date)), "YYYY-MM-DD HH:mm:ss", true);
      if (d.isValid()) {
        sDate = d.subtract(2, "minute");
        setStartDate(sDate);
      }
    }

    let eDate = endDate;
    if (to_date) {
      const d = dayjs(normalizeUrlDate(decodeURIComponent(to_date)), "YYYY-MM-DD HH:mm:ss", true);
      if (d.isValid()) {
        eDate = d.add(2, "minute");
        setEndDate(eDate);
      }
    }

    if (from_date || to_date || search_term) {
      getPositions(
        limitValue,
        1,
        search,
        type,
        column,
        sort,
        fmtLocal(sDate),
        fmtLocal(eDate)
      );
      setCurrentPage(1);
      setSearch(s);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from_date, to_date, search_term]);

  const apiData = reportData || [];

  let prevCumulativeDistance: number;
  let cumulativeSum = 0;

  // calcule distance cumulée
  const lineChartResult = apiData.map((item: any) => {
    if (prevCumulativeDistance == null) {
      prevCumulativeDistance = item.GPSDIST;
    }
    const difference = item.GPSDIST - prevCumulativeDistance;
    cumulativeSum += difference;
    prevCumulativeDistance = item.GPSDIST;
    return {
      ...item,
      cumulativeDifference: cumulativeSum / 1000, // km
    };
  });

  const transformedSOGData = apiData.map((item: any) => [
    new Date(item.TIMESTAMP).getTime(),
    item.SOG,
  ]);

  const transformedEngineStatData = apiData.map((item: any) => [
    new Date(item.TIMESTAMP).getTime(),
    item.ENGINESTAT,
  ]);

  const handlePointClick = (lat: number, lng: number) => {
    setModalInfo({ show: true, lat, lng });
  };

  const sampleData: ChartData = {
    chartTitle: translate("Diagramme de distance, vitesse et contact"),
    chartTitleAlign: "center",
    enabledLegend: true,
    height: 400,
    series: [
      {
        name: translate("Vitesse"),
        unit: " km/h",
        description: translate("Vitesse"),
        data: transformedSOGData,
        color: "#41b346",
        type: "spline",
        yAxisState: true,
        labels: { format: "{value}", style: { color: "#41b346" } },
        events: {
          click: (event: any) => {
            const point = event.point;
            const index = point.index;
            const item = apiData[index];
            setModalInfo({ show: true, lat: item.LAT, lng: item.LNG });
          },
        },
      },
      {
        name: translate("Engine Stat"),
        unit: "",
        description: translate("Engine Stat"),
        data: transformedEngineStatData,
        color: "#EB1D36",
        type: "spline",
        yAxis: 1,
        yAxisState: true,
        labels: { format: "{value}", style: { color: "#EB1D36" } },
        events: {
          click: (event: any) => {
            const point = event.point;
            const index = point.index;
            const item = apiData[index];
            setModalInfo({ show: true, lat: item.LAT, lng: item.LNG });
          },
        },
      },
      {
        name: translate("Distance"),
        unit: " km",
        description: translate("Distance"),
        data: lineChartResult.map((item: any) => [
          new Date(item.TIMESTAMP).getTime(),
          item.cumulativeDifference,
        ]),
        color: "#0080FF",
        type: "spline",
        yAxisState: true,
        labels: { format: "{value}", style: { color: "#0080FF" } },
        events: {
          click: (event: any) => {
            const point = event.point;
            const index = point.index;
            const item = apiData[index];
            setModalInfo({ show: true, lat: item.LAT, lng: item.LNG });
          },
        },
      },
    ],
  };

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(
      "Diagramme de distance, vitesse et contact"
    );

    const titleRow = worksheet.addRow(["", "Diagramme de distance, vitesse et contact:", ""]);
    titleRow.getCell(2).font = { bold: true, size: 14, name: "Arial" };
    titleRow.getCell(2).alignment = { vertical: "middle", horizontal: "center" };
    titleRow.eachCell((cell) => {
      cell.font = { bold: true, size: 14, name: "Arial" };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      if (typeof cell.value === "string") cell.value = cell.value.toUpperCase();
    });

    const creationDateRow = worksheet.addRow([
      "Creation Date:",
      new Date(reportData.moreInfo?.date_creation).toLocaleString(),
    ]);
    creationDateRow.eachCell((cell) => {
      cell.font = { color: { argb: "0000FF" } };
    });

    const vehicleImmatriculationRow = worksheet.addRow([
      "Immatriculation Véhicule:",
      reportData.moreInfo?.immatriculation_vehicule || "N/A",
      "Turn Report:",
      reportData.moreInfo?.turn_report || "N/A",
    ]);
    vehicleImmatriculationRow.eachCell((cell) => {
      cell.font = { color: { argb: "0000FF" } };
    });
    worksheet.mergeCells("A5:H5");

    const headerStyle: Partial<Style> = {
      fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFCCCCCC" } },
      font: { bold: true, size: 10, name: "Arial", underline: false, italic: false, vertAlign: undefined, outline: false, strike: false, color: { argb: "FFFFFFFF" } },
    };

    worksheet.addRow([
      "Date/Time Start",
      "Date/Time End",
      "Start Distance",
      "End Distance",
      "Difference",
      "Average Speed (km/h)",
      "Max Speed (km/h)",
      "Speed Status",
    ]);
    worksheet.getRow(6).eachCell({ includeEmpty: true }, (cell) => {
      cell.style = headerStyle;
    });

    const apiData = reportData || [];
    const row = worksheet.addRow([
      new Date(reportData.moreInfo?.date_debut).toLocaleString(),
      new Date(reportData.moreInfo?.date_fin).toLocaleString(),
      apiData.length > 0 ? `${apiData[0].distance} KM ` : "No Data",
      apiData.length > 0 ? `${apiData[apiData.length - 1].distance} KM ` : "No Data",
      apiData.length > 0 ? `${apiData[apiData.length - 1].distance - apiData[0].distance} KM` : "No Data",
      reportData.additionalData?.average_speed != null ? Number(reportData.additionalData.average_speed).toFixed(2) : "0",
      reportData.additionalData?.max_speed?.toFixed?.(2),
      reportData.additionalData?.max_speed > 90 ? "Excess Speed" : "No Excess Speed",
    ]);

    const speedStatusCell = row.getCell(8);
    speedStatusCell.font = { color: { argb: reportData.additionalData?.max_speed > 90 ? "FFFF0000" : "FF00FF00" } };

    worksheet.columns.forEach((column) => {
      column.width = Math.max(column.width ?? 0, 30);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const dateString = new Date().toISOString().split("T")[0];
    const fileName = `Diagramme de distance, vitesse et contact_${dateString}.xlsx`;
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
  };

  const renderMaxSpeedStatus = () => {
    if (reportData.additionalData?.max_speed > 90) {
      return (
        <>
          <i className="las la-tachometer-alt" style={{ color: "red" }}></i>
          <span style={{ color: "red" }}> {translate("Excès de vitesse")}</span>
        </>
      );
    } else {
      return (
        <>
          <i className="las la-tachometer-alt" style={{ color: "green" }}></i>
          <span style={{ color: "green" }}> {translate("Pas d'excès de vitesse")}</span>
        </>
      );
    }
  };

  const menuItems = [
    "Matriculation",
    "PSN"
  ];

  const handleStartDateChange = (newDate: Dayjs | null) => {
    if (newDate) {
      if (newDate.isAfter(endDate)) {
        console.warn("⚠️ La date de début ne peut pas dépasser la date de fin !");
        return;
      }
      setStartDate(newDate);
    }
  };

  const handleEndDateChange = (newDate: Dayjs | null) => {
    if (newDate) {
      if (newDate.isBefore(startDate)) {
        console.warn("⚠️ La date de fin ne peut pas être avant la date de début !");
        return;
      }
      setEndDate(newDate);
    }
  };

  const handleTypeSearch = (option: string) => {
    const translated = translate(option);
    setSelectedSearch(translated);


    switch (translated) {
      case translate("Matriculation"):
        setSelectedSearch(translate("Matriculation"));
        setType(0);
        break;
      case translate("PSN"):
        setSelectedSearch(translate("PSN"));
        setType(1);
        break;
      default:
        break;
    }
    setTypeSearch(translate(translated));
  };

  const handleTypeAdvancedSearch = async (event: any) => {
    const newValue = event.target.value;
    setSearch(newValue);
  };

  const handleAdvancedSearch = async (event: any) => {
    const newValue = event.target.value;
    console.log(newValue);


    await getPositions(
      limitValue,
      1,
      search,
      type,
      column,
      sort,
      fmtLocal(startDate),
      fmtLocal(endDate)
    );
    setCurrentPage(1);
  };

  const handleResetSearch = async () => {
    setSearch("");
    // await getFleet(limit, page, search, type, column, sort, filters)
  };

  const handleSelectChange = async (event: any) => {
    const newValue = event.target.value;
    setCurrentPage(1);
    setSelectedLimitValue(newValue);
    const commentsFormServer = await getPositions(
      parseInt(newValue),
      1,
      search,
      type,
      column,
      sort,
      fmtLocal(startDate),
      fmtLocal(endDate)
    );
    setPositions(commentsFormServer);
    window.scrollTo(0, 0);
  };

  const fetchAddress = async (LAT: number, LON: number) => {
    try {
      return await getAddressFromCoordinates(LAT, LON);
    } catch (error) {
      console.error("Erreur lors de la récupération de l'adresse", error);
      return "Adresse non disponible";
    }
  };

  const [addresses, setAddresses] = useState<{ [key: number]: string }>({});
  useEffect(() => {
    const fetchAllAddresses = async () => {
      const newAddresses: { [key: number]: string } = {};
      for (const position of positions) {
        if (position.LAT && position.LON) {
          const address = await fetchAddress(position.LAT, position.LON);
          newAddresses[position.id_position] = address;
        }
      }
      setAddresses(newAddresses);
    };
    if (positions.length > 0) {
      fetchAllAddresses();
    }
  }, [positions]);

  const [markerCoordinates, setMarkerCoordinates] = useState({ LAT: 0, LON: 0, });
  const [showMapModal, setShowMapModal] = useState(false);
  const handleMapIconClick = (LAT: number, LON: number) => {
    setMarkerCoordinates({ LAT, LON });
    setShowMapModal(true);
  };

  const handleColumnChange = (column: string) => {
    setSelectedColumns((prevState: any) => ({
      ...prevState,
      [column]: !prevState[column],
    }));
  };
  const handleSortingColum = (currentColumn: string) => {
    sort == "DESC" ? setSort("ASC") : setSort("DESC");
    setSortColumn(currentColumn);
    setCurrentPage(1)
  };

  const handlePageClick = async (data: any) => {
    let currentPage = data.selected + 1;
    await getPositions(
      limitValue,
      currentPage,
      search,
      type,
      column,
      sort,
      fmtLocal(startDate),
      fmtLocal(endDate)
    );
    setCurrentPage(currentPage)
    window.scrollTo(0, 0)
  };

  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const { copyToClipboard, copiedId } = useClipboard(translate("Matriculation Copied"));
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isVehiclesSelected, setIsPositionsSelected] = useState(false);


  useEffect(() => {
    // If the list of selected POIs is empty, disable general selection
    if (selectedPositions.length === 0 && selectAll) {
      setSelectAll(false);
    }
  }, [selectedPositions, selectAll]);



  const handlePositionsSelect = (PositionID: string) => {
    let updatedSelectedPositions: string[] = [];

    // If "Select All Vehicles" is enabled, selects or deselects all vehicles
    if (selectAll) {
      updatedSelectedPositions = selectedPositions.includes(PositionID)
        ? selectedPositions.filter(id => id !== PositionID) //Deselect if already selected
        : positions.map(position => position.id_position.toString()); // Select all vehicles
    } else {
      //Managing selection/normal selection of an individual vehicle
      if (selectedPositions.includes(PositionID)) {
        updatedSelectedPositions = selectedPositions.filter(id => id !== PositionID);
      } else {
        updatedSelectedPositions = [...selectedPositions, PositionID];
      }
    }

  // Updates the list of selected vehicles
    setSelectedPositions(updatedSelectedPositions);

    // Updates the Vehicles Selected state (activate if at least one is selected)
    setIsPositionsSelected(updatedSelectedPositions.length > 0);

    // console.log(updatedSelectedVehicles);
  };


  // In the handleSelectAllDrivers function
  const handleSelectAllPositions = (checked: boolean) => {
    setSelectAll(checked);
    // console.log(checked)
    if (checked) {
      // Select all POIs
      const allPositionIDs = positions.map((position) => position.id_position.toString()); // Convert to strings
      setSelectedPositions(allPositionIDs);
      setIsPositionsSelected(true);// Mark as selected
    } else {
      // Select all POIs
      setSelectedPositions([]);
      setIsPositionsSelected(false); // Mark as unselected
    }
  };


  const positionHeaders = [
    translate("PSN"),
    translate("ENGINESTAT"),
    translate("Date"),
    translate("Speed (km/h)"),
    translate("Odometer (km)"),
    translate("Position"),
    translate("GSM Level"),
    translate("Satellites"),
    translate("Validity")
  ];

  const getVehicleState = (engineStat: number, sog: number): string => {
    if (engineStat === 0 && sog === 0) {
      return "Parking"; // moteur éteint
    }
    if (engineStat === 1 && sog < 5) {
      return "Pause"; // moteur allumé mais quasi immobile
    }
    if (engineStat === 1 && sog >= 5) {
      return "En mouvement"; // moteur allumé et véhicule roule
    }
    return "Inconnu"; // fallback
  };

  const downloadVehicleExcel = () => {

    const selectedData = positions.filter((position) =>
      selectedPositions.includes(position.id_position.toString())
    ).map((position) => [
      position.PSN,
      getVehicleState(position.ENGINESTAT, position.SOG),
      toTimestamp(position.TIMESTAMP),
      position.SOG,
      (position.GPSDIST / 1000).toFixed(2),
      `${position.LAT}, ${position.LON}`, // correction ici
      position.GSMLVL,
      position.GUS,
      position.NST === 1 ? "Valid" : "Invalid"
    ]);


    generateExcelFile(translate("List") + ' ' + translate("Position"), positionHeaders, selectedData);
  };

  const downloadVehiclePDF = () => {

    const selectedData = positions.filter((position) =>
      selectedPositions.includes(position.id_position.toString())
    ).map((position) => [
      position.PSN,
      getVehicleState(position.ENGINESTAT, position.SOG),
      toTimestamp(position.TIMESTAMP),
      position.SOG,
      (position.GPSDIST / 1000).toFixed(2),
      `${position.LAT}, ${position.LON}`,
      position.GSMLVL,
      position.GUS,
      position.NST === 1 ? "Valid" : "Invalid"
    ]);
    generatePDFFile(translate("List") + ' ' + translate("Position"), positionHeaders, selectedData);
  };


  const onDownloadConfirm = (format: string) => {
    if (selectedPositions.length > 0) {
      handleDownloadConfirm(format, downloadVehicleExcel, downloadVehiclePDF);
    } else {
      toast.warn("Please select at least one vehicle", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };



  return (
    <>
      <div style={{ marginTop: "10px" }}>
        <h5>{translate("Positions tracking")} {total}</h5>
        <p>
          <button
            className="btn btn-outline-info"
            style={{ position: "absolute", top: "0", right: "0" }}
            onClick={() => setShowDownloadModal(true)}
          >
            <i className="las la-download"></i>
            {translate("Export")}
          </button>
        </p>
      </div>

      <div style={{ marginTop: "10px", marginLeft: "0px", position: "relative" }}>
        <Row style={{ marginBottom: "15px" }}>
          <div className="col-md-3" style={{ margin: "0px 0px 10px 0px" }}>
            <div>{translate("Positions search")}</div>
            <div className="input-group">
              <Dropdown>
                <Dropdown.Toggle variant="link" id="dropdown-custom" className="custom-toggle">
                  <i className="fas fa-chevron-down" style={{ fontSize: "20" }}></i>
                </Dropdown.Toggle>
                <Dropdown.Menu >
                  {menuItems.map(option => (
                    <Dropdown.Item
                      key={option}
                      className={`${selectedSearch == translate(option) ? 'active' : ''}`}
                      onClick={() => handleTypeSearch(option)}
                    >
                      {translate(option)}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              <input
                type="text"
                placeholder={` ${translate("Search by")} ${translate(typeSearch)}`}
                value={search}
                onChange={handleTypeAdvancedSearch}
                className="form-control"
              />
              <Button
                variant="secondary"
                onClick={handleAdvancedSearch}
                className="btn-reset"
                style={{ marginRight: " 4px" }}
              >
                <i className="las la-search" style={{ color: "#fff" }}></i>
              </Button>
              <Button
                variant="secondary"
                onClick={handleResetSearch}
                className="btn-reset"
              >
                <i className="las la-times" style={{ color: "#fff" }}></i>
              </Button>
            </div>
          </div>

          <Col md={3} >
            <div>{translate("Start date")}</div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <i className="las la-calendar d-inline icon-report" style={{ marginRight: "0px", fontSize: "1.2rem" }}></i>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
                <DateTimePicker
                  value={startDate}
                  onChange={handleStartDateChange}
                  ampm={false}
                  format="YYYY-MM-DD HH:mm:ss"
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          height: 40,
                          fontSize: "14px",
                          "& fieldset": { borderColor: "#ddd" },
                          "&:hover fieldset": { borderColor: "#e76f33" },
                          "&.Mui-focused fieldset": { borderColor: "#d95c1e" },
                        },
                        "& .MuiInputLabel-root": { color: "#ff7e41" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#d95c1e" },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          </Col>

          <Col md={3}>
            <div>{translate("End date")}</div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <i className="las la-calendar d-inline icon-report" style={{ marginRight: "0px", fontSize: "1.2rem" }}></i>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
                <DateTimePicker
                  value={endDate}
                  onChange={handleEndDateChange}
                  ampm={false}
                  format="YYYY-MM-DD HH:mm:ss"
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          height: 40,
                          fontSize: "14px",
                          "& fieldset": { borderColor: "#ddd" },
                          "&:hover fieldset": { borderColor: "#e76f33" },
                          "&.Mui-focused fieldset": { borderColor: "#d95c1e" },
                        },
                        "& .MuiInputLabel-root": { color: "#ff7e41" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#d95c1e" },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          </Col>

          <div className="col-md-3 d-flex justify-content-end align-items-center">
            <div className="dataTables_length" id="DataTables_Table_0_length">
              <label style={{ marginBottom: "0" }}>
                {translate("Show")}
                <select
                  name="DataTables_Table_0_length"
                  aria-controls="DataTables_Table_0"
                  className="custom-select custom-select-sm form-control form-control-sm ml-2"
                  style={{ width: "66px" }}
                  onChange={handleSelectChange}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="500">500</option>
                </select>
              </label>
            </div>
            <Dropdown>
              <Dropdown.Toggle variant="" id="dropdown-basic" title={translate("Display columns")}>
                <i className="las la-eye"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item as="button" style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedColumns.id_vehicule}
                    onChange={() => handleColumnChange("id_vehicule")}
                  />
                  <span style={{ marginLeft: "10px" }}>{translate("ID")}</span>
                </Dropdown.Item>
                <Dropdown.Item as="button" style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedColumns.immatriculation_vehicule}
                    onChange={() => handleColumnChange("immatriculation_vehicule")}
                  />
                  <span style={{ marginLeft: "10px" }}>{translate("Matriculation")}</span>
                </Dropdown.Item>
                <Dropdown.Item as="button" style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedColumns.PSN}
                    onChange={() => handleColumnChange("PSN")}
                  />
                  <span style={{ marginLeft: "10px" }}>{translate("PSN")}</span>
                </Dropdown.Item>
                <Dropdown.Item as="button" style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedColumns.username_user}
                    onChange={() => handleColumnChange("username_user")}
                  />
                  <span style={{ marginLeft: "10px" }}>{translate("User")}</span>
                </Dropdown.Item>
                <Dropdown.Item as="button" style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedColumns.nom_groupe}
                    onChange={() => handleColumnChange("nom_groupe")}
                  />
                  <span style={{ marginLeft: "10px" }}>{translate("Group")}</span>
                </Dropdown.Item>
                <Dropdown.Item as="button" style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedColumns.GPSDIST}
                    onChange={() => handleColumnChange("GPSDIST")}
                  />
                  <span style={{ marginLeft: "10px" }}>{translate("Odometer")}</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Row>
      </div>

      <>
        <div className="row"></div>
        <div>
          <div className="row"></div>
          <div className="row">
            <div className="table-responsive">
              <Table className="dataTable">
                <thead className="bg-white text-uppercase">
                  <tr className="ligth ligth-data">
                    <th>
                      <div className="checkbox d-inline-block">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectAll}
                          onChange={(e) => handleSelectAllPositions(e.target.checked)}

                        />
                        <label className="form-check-label"></label>
                      </div>
                    </th>
                    {selectedColumns.id_vehicule && (<th className="sorting" onClick={() => handleSortingColum("id_vehicule")}> {translate("Date")} </th>)}
                    {selectedColumns.vehicule_type && (<th className="sorting" onClick={() => handleSortingColum("vehicule_type")}>  {translate("Type")} </th>)}
                    {selectedColumns.GPSDIST && (<th className="sorting" onClick={() => handleSortingColum("GPSDIST")}> {translate("Odometer")}  </th>)}
                    <th>{translate('Statute')}</th>
                    <th>{translate('Actions')}</th>
                  </tr>
                </thead>
                <tbody key="#" className="ligth-body">
                  {loading ? (
                    <tr style={{ textAlign: "center" }}>
                      <td className="text-center" colSpan={10}>
                        <p><PropagateLoader color={"#123abc"} loading={loading} size={20} /></p>
                      </td>
                    </tr>
                  ) : positions.length > 0 ? (
                    positions.map((position) => (
                      <tr key={position.id_position} className={position.id_position.toString()}>
                        <td>
                          <div className="checkbox d-inline-block">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`checkbox-${position["id_position"]}`}
                              checked={selectedPositions.includes(position.id_position.toString())}
                              onChange={() => handlePositionsSelect(position.id_position.toString())}
                            />
                            <label htmlFor={`checkbox-${position.id_position}`} className="mb-0"></label>
                          </div>
                        </td>
                        {selectedColumns.id_vehicule && <td>{toTimestamp(position.TIMESTAMP)}</td>}
                        {selectedColumns.id_vehicule && (
                          <td>
                            <div className="align-items-center">
                              <div>
                                {"Satellites: "} {position.GUS} <br />
                                {"Speed: "}{position.SOG}  {"Km/h"}
                                <p className="mb-0">
                                  <small>{addresses[position.id_position] || "Loading..."}</small>
                                </p>
                              </div>
                            </div>
                          </td>
                        )}
                        {selectedColumns.GPSDIST && <td>{Distance(position.GPSDIST)}</td>}
                        <td>
                          {position["GSMLVL"] !== null ? position["GSMLVL"].toFixed(0) : "0"}%{" "}
                          {position["GSMLVL"] !== null && (
                            <i
                              style={{ color: BarreReseau(position["GSMLVL"]).split("color:")[1].slice(0, -1) }}
                              className={`las la-signal ${BarreReseau(position["GSMLVL"])}`}
                            ></i>
                          )}
                          {position["NST"] === 1 ? (
                            <i className={ValidatePosition(position["NST"])}></i>
                          ) : (
                            <i className={ValidatePosition(position["NST"])}></i>
                          )}
                          <img
                            src={engineStat(position["ENGINESTAT"], position["SOG"], translate).iconState}
                            alt=""
                            title={engineStat(position["ENGINESTAT"], position["SOG"], translate).tooltipMessage}
                          />
                        </td>
                        <td>
                          <div className="d-flex align-items-center list-action">
                            <i
                              className="las la-map-marker-alt"
                              onClick={() => handleMapIconClick(position.LAT, position.LON)}
                              style={{ cursor: "pointer", marginLeft: "5px", fontSize: "20px", color: "red" }}
                            ></i>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9}>No positions available</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 d-flex align-items-center">
            <span>
              {translate("Displaying")} {positions.length} {translate("out of")} {total}
            </span>
          </div>
          <div className="col-md-6">
            <ReactPaginate
              previousLabel={translate("previous")}
              nextLabel={translate("next")}
              breakLabel={"..."}
              pageCount={pageCount}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={handlePageClick}
              containerClassName={"pagination justify-content-end"}
              pageClassName={"page-item"}
              pageLinkClassName={"page-link"}
              previousClassName={"page-item"}
              previousLinkClassName={"page-link"}
              nextClassName={"page-item"}
              nextLinkClassName={"page-link"}
              breakClassName={"page-item"}
              breakLinkClassName={"page-link"}
              activeClassName={"active"}
              forcePage={currentPage - 1}
            />
          </div>
        </div>

        <MapModal
          show={showMapModal}
          onClose={() => setShowMapModal(false)}
          lat={markerCoordinates.LAT}
          lng={markerCoordinates.LON}
        />
      </>

      {/* Graphique */}
      <div style={{ minHeight: "400px" }}>
        <Linecharts {...sampleData} />
      </div>

      <MapModal
        show={modalInfo.show}
        onClose={() => setModalInfo({ show: false, lat: 0, lng: 0 })}
        lat={modalInfo.lat}
        lng={modalInfo.lng}
      />
      <DownloadModal
        show={showDownloadModal}
        onHide={() => setShowDownloadModal(false)}
        onDownloadConfirm={onDownloadConfirm}
      />
    </>
  );
}